/**
 * Shared first-time-user error guidance for Employees, Attendance, Leave, Payroll.
 * Returns NoticeCard-compatible payloads: { title, message, hint, steps, guideLinks }.
 */

export const GUIDE_LINKS = {
  setup: { label: "Organisation Setup", to: "/Setup" },
  employees: { label: "People Hub", to: "/employees" },
  employeeAdd: { label: "Add Employee", to: "/employees/add" },
  employeeImport: { label: "Import Employees", to: "/employees?module=employees&view=import" },
  employeeImportGuide: { label: "Import Guide", to: "/employee-import/guide" },
  employmentTypes: { label: "Employee Types", to: "/settings/employee-configuration#employee-types" },
  attendance: { label: "Attendance Hub", to: "/attendance" },
  attendanceSetup: { label: "Attendance Setup", to: "/attendance/setup" },
  attendanceConfig: { label: "Attendance Configuration", to: "/attendance/configuration" },
  attendanceRecords: { label: "Attendance Records", to: "/attendance/records" },
  shifts: { label: "Shift Management", to: "/attendance/shifts" },
  holidays: { label: "Holiday Calendar", to: "/holiday-calendar" },
  leave: { label: "Leave Dashboard", to: "/leave" },
  leaveAdmin: { label: "Leave Settings", to: "/settings/leave-setup" },
  leaveApply: { label: "Apply Leave", to: "/leave?tab=apply" },
  leaveBalances: { label: "Leave Balances", to: "/leave?tab=balances" },
  payrollSettings: { label: "3.1 Payroll Settings", to: "/salary-v2/setup" },
  payrollComponents: { label: "3.2 Salary Components", to: "/salary-v2/components" },
  payrollStructures: { label: "3.3 Salary Structures", to: "/salary-v2/structures" },
  payrollPayGroups: { label: "3.4 Pay Groups", to: "/salary-v2/pay-groups" },
  assignSalary: { label: "Assign Salary Structure", to: "/employeesalarystructure" },
  processPayroll: { label: "Process Payroll", to: "/process-payroll" },
  settings: { label: "Settings", to: "/settings" },
};

function extractMessage(errorOrText) {
  if (!errorOrText) return "";
  if (typeof errorOrText === "string") return errorOrText.trim();
  if (typeof errorOrText === "object") {
    if (errorOrText.title && errorOrText.steps && errorOrText.message) {
      return String(errorOrText.message).trim();
    }

    const detail = errorOrText?.response?.data?.detail ?? errorOrText?.detail;
    if (typeof detail === "string") return detail.trim();
    if (Array.isArray(detail)) {
      return detail
        .map((d) => (typeof d === "string" ? d : d?.msg || d?.message || ""))
        .filter(Boolean)
        .join("; ");
    }
    if (detail && typeof detail === "object") {
      if (typeof detail.message === "string") return detail.message;
      if (typeof detail.detail === "string") return detail.detail;
      try {
        return JSON.stringify(detail).slice(0, 280);
      } catch {
        return "";
      }
    }

    const plain = errorOrText?.response?.data?.message || errorOrText?.hint;
    if (typeof plain === "string" && plain.trim()) return plain.trim();

    const msg = errorOrText?.message;
    if (typeof msg === "string" && msg.trim() && msg !== "[object Object]") return msg.trim();
  }
  return String(errorOrText);
}

/** Normalize axios / API errors into a user-readable string. */
export function extractApiErrorMessage(errorOrText, fallback = "Request failed") {
  const message = extractMessage(errorOrText);
  return message || fallback;
}

function notice({ title, message, hint, steps, guideLinks }) {
  return {
    title,
    message,
    hint: hint || null,
    steps: (steps || []).filter(Boolean),
    guideLinks: (guideLinks || []).filter((l) => l?.label && l?.to),
  };
}

function primary(link) {
  return { ...link, primary: true };
}

/** Driver, SQL, and server dumps are not something a person can act on. */
export function isTechnicalError(message) {
  const m = String(message || "").toLowerCase();
  if (!m) return false;
  return (
    m.includes("sqlalchemy")
    || m.includes("asyncpg")
    || m.includes("psycopg")
    || m.includes("programmingerror")
    || m.includes("undefinedcolumn")
    || m.includes("undefinedtable")
    || m.includes("[sql:")
    || m.includes("insert into ")
    || m.includes("traceback")
    || m.includes("internal server error")
    || m.includes("status code 500")
    || (m.includes("does not exist") && (m.includes("column") || m.includes("relation")))
  );
}

function technicalNotice(domain) {
  if (domain === "leave") {
    return notice({
      title: "This leave could not be saved",
      message: "Nothing was assigned. The form is still filled in, so you can try again.",
      hint: "If the same message comes back, the leave system needs an update. That is an administrator task. The employee and dates you chose are not the problem.",
      steps: [
        "Submit this leave once more.",
        "Open Leave Balances and confirm this employee still has days for the selected leave type.",
        "If it fails again, ask an administrator to update the leave system, then try once more.",
      ],
      guideLinks: [primary(GUIDE_LINKS.leaveBalances), GUIDE_LINKS.leave],
    });
  }
  if (domain === "attendance") {
    return notice({
      title: "This attendance change could not be saved",
      message: "The update did not go through. The day you selected is unchanged.",
      hint: "Try the same action again. If it keeps failing, an administrator needs to update the system.",
      steps: [
        "Try the same attendance action once more.",
        "Confirm the employee and date, then retry.",
        "If it still fails, ask an administrator for help.",
      ],
      guideLinks: [primary(GUIDE_LINKS.attendance), GUIDE_LINKS.attendanceRecords],
    });
  }
  if (domain === "employee") {
    return notice({
      title: "This employee could not be saved",
      message: "The save did not go through. The details you entered are still on the form.",
      hint: "Try again. If the same message appears, an administrator needs to update the system.",
      steps: [
        "Check the highlighted fields and save again.",
        "If it fails again, ask an administrator for help.",
      ],
      guideLinks: [primary(GUIDE_LINKS.employees), GUIDE_LINKS.employeeAdd],
    });
  }
  if (domain === "payroll") {
    return notice({
      title: "This payroll action could not be finished",
      message: "The step did not complete. Payroll was not changed by this attempt.",
      hint: "Try again. If the same message appears, an administrator needs to update the system.",
      steps: [
        "Retry the payroll action.",
        "If it fails again, ask an administrator for help.",
      ],
      guideLinks: [primary(GUIDE_LINKS.processPayroll), GUIDE_LINKS.payrollSettings],
    });
  }
  return notice({
    title: "This action could not be finished",
    message: "Something unexpected stopped the save. Nothing was changed.",
    hint: "Try again. If the message stays, ask an administrator to check the system.",
    steps: [
      "Try the same action once more.",
      "If it fails again, tell an administrator what you were doing.",
    ],
    guideLinks: [primary(GUIDE_LINKS.settings)],
  });
}

/** Employee create / import / list setup gaps */
function parseEmployee(message) {
  const m = message.toLowerCase();

  if (m.includes("email") && (m.includes("required") || m.includes("already") || m.includes("exist"))) {
    return notice({
      title: "Work email needs a fix",
      message,
      hint: "Each employee must have a unique work email.",
      steps: [
        "Go to Step 1 — Basic Details",
        "Enter a different work email that is not already used",
        "Or open People Hub and edit the existing employee instead",
        "Return here and click Finish again",
      ],
      guideLinks: [primary(GUIDE_LINKS.employees), GUIDE_LINKS.employeeAdd],
    });
  }

  if (
    (m.includes("mobile") || m.includes("phone"))
    && (m.includes("already") || m.includes("exist") || m.includes("duplicate") || m.includes("unique"))
  ) {
    return notice({
      title: "Mobile number already in use",
      message,
      hint: "Two employees cannot share the same mobile number.",
      steps: [
        "Go to Step 1 — Basic Details",
        "Enter a different mobile number",
        "Or open People Hub if this person was already added",
        "Click Finish again",
      ],
      guideLinks: [primary(GUIDE_LINKS.employees), GUIDE_LINKS.employeeAdd],
    });
  }

  if (m.includes("employee code") && (m.includes("already") || m.includes("exist") || m.includes("unique"))) {
    return notice({
      title: "Employee code already used",
      message,
      hint: "The previewed code was taken — refresh or change the prefix.",
      steps: [
        "Go to Step 1 — Basic Details",
        "Change the letter prefix (e.g. EMP → HR) or wait for the next code to refresh",
        "Click Finish again",
      ],
      guideLinks: [GUIDE_LINKS.employeeAdd],
    });
  }

  if (m.includes("uan") && (m.includes("already") || m.includes("in use") || m.includes("exist"))) {
    return notice({
      title: "UAN already in use",
      message,
      hint: "UAN must be unique for each employee in your organisation.",
      steps: [
        "Stay on Step 5 — Statutory",
        "Enter a different UAN, or clear it and click Finish to proceed without statutory",
        "Click Finish again",
      ],
      guideLinks: [GUIDE_LINKS.employeeAdd],
    });
  }

  if (m.includes("pan") && (m.includes("already") || m.includes("in use") || m.includes("exist") || m.includes("format") || m.includes("invalid"))) {
    return notice({
      title: "PAN needs a fix",
      message,
      hint: "PAN must be valid and unique for each employee.",
      steps: [
        "Stay on Step 5 — Statutory",
        "Correct the PAN (format like ABCDE1234F), or clear it and click Finish",
        "Click Finish again",
      ],
      guideLinks: [GUIDE_LINKS.employeeAdd],
    });
  }

  if (m.includes("aadhaar") && (m.includes("already") || m.includes("in use") || m.includes("exist") || m.includes("invalid") || m.includes("digit") || m.includes("checksum"))) {
    return notice({
      title: "Aadhaar needs a fix",
      message,
      hint: "Aadhaar must be a real 12-digit number (checksum is checked) and unique in your organisation. Leave it blank if you do not have it yet.",
      steps: [
        "Stay on Step 5 — Statutory",
        "Correct the Aadhaar number, or clear it and click Finish",
        "Click Finish again",
      ],
      guideLinks: [GUIDE_LINKS.employeeAdd],
    });
  }

  if (
    (m.includes("pf member") || m.includes("pf_number") || m.includes("pf number"))
    && (m.includes("already") || m.includes("in use") || m.includes("invalid") || m.includes("format") || m.includes("failed"))
  ) {
    return notice({
      title: "PF Member ID needs a fix",
      message,
      hint: "PF Member ID must be unique and use letters, numbers, / or - only (max 30 characters).",
      steps: [
        "Stay on Step 5 — Statutory",
        "Correct the PF Member ID, or clear it and click Finish",
        "Click Finish again",
      ],
      guideLinks: [GUIDE_LINKS.employeeAdd],
    });
  }

  if (
    m.includes("already registered")
    || m.includes("already in use by another employee")
    || (m.includes("duplicate") && m.includes("account"))
    || (m.includes("account number") && (m.includes("already") || m.includes("another employee")))
  ) {
    return notice({
      title: "Duplicate bank account number",
      message,
      hint: "The same account number cannot be used for two employees. Enter a different account number for this person.",
      steps: [
        "Stay on Step 4 — Banking",
        "Clear the Account number and Confirm account number fields",
        "Enter this employee’s own bank account number (not one already used elsewhere)",
        "Click Save banking details or Next again",
      ],
      guideLinks: [GUIDE_LINKS.employees],
    });
  }

  if (
    m.includes("account number")
    || m.includes("bank account")
    || m.includes("banking details")
    || m.includes("fix the banking")
    || m.includes("account holder")
    || m.includes("bank branch")
    || (m.includes("bank") && (m.includes("invalid") || m.includes("match") || m.includes("already") || m.includes("empty") || m.includes("not saved")))
    || m.includes("confirm account")
    || m.includes("ifsc")
  ) {
    const isEmptyForm = m.includes("still empty") || m.includes("form is still empty");
    return notice({
      title: isEmptyForm ? "Bank account not entered yet" : "Banking details were not saved",
      message,
      hint: isEmptyForm
        ? "Nothing was saved yet — the Banking form is empty. Fill IFSC, account number, confirm account number, and holder name."
        : "The bank account was not written because one or more fields are missing or invalid. The list above names each problem.",
      steps: [
        "Stay on Step 4 — Banking (scroll to the form below this message)",
        "Look up IFSC, then enter account holder name, account number, and confirm account number",
        "Click Save banking details (or Next) after every required field is filled",
        "When the account card appears with Verified / Disbursement-ready, continue to Statutory",
      ],
      guideLinks: [GUIDE_LINKS.employees, GUIDE_LINKS.employeeAdd],
    });
  }

  if (
    m.includes("salary")
    || m.includes("template")
    || (m.includes("ctc") && (m.includes("invalid") || m.includes("greater") || m.includes("required")))
  ) {
    return notice({
      title: "Compensation needs a fix",
      message,
      hint: "Choose an active salary structure and a valid annual CTC.",
      steps: [
        "Go to Step 3 — Compensation",
        "Select an active salary structure and enter a valid CTC",
        "Click Finish again",
      ],
      guideLinks: [GUIDE_LINKS.payrollStructures, GUIDE_LINKS.employeeAdd],
    });
  }

  if (
    (m.includes("department") || m.includes("designation") || m.includes("location") || m.includes("manager"))
    && (m.includes("invalid") || m.includes("required") || m.includes("belong") || m.includes("active"))
  ) {
    return notice({
      title: "Organisation details need a fix",
      message,
      hint: "Department, designation, location, or manager selection is invalid.",
      steps: [
        "Go to Step 2 — Organization",
        "Re-select department, designation, location, and manager",
        "If options are missing, add them in Organisation Setup first",
        "Click Finish again",
      ],
      guideLinks: [primary(GUIDE_LINKS.setup), GUIDE_LINKS.employeeAdd],
    });
  }

  if (m.includes("organisation setup") || m.includes("foundation") || m.includes("complete organisation")) {
    return notice({
      title: "Organisation setup incomplete",
      message,
      hint: "Finish Organisation Setup before adding employees.",
      steps: [
        "Open Organisation Setup and complete the required steps",
        "Add departments, designations, and locations if missing",
        "Return here and save the employee again",
      ],
      guideLinks: [primary(GUIDE_LINKS.setup), GUIDE_LINKS.employeeAdd],
    });
  }

  if (m.includes("import") || m.includes("csv") || m.includes("upload") || m.includes("row")) {
    return notice({
      title: "Employee import needs correction",
      message,
      hint: "Fix the highlighted rows or columns, then upload again.",
      steps: [
        "Open the Import Guide and match your file columns to the template",
        "Confirm departments / designations / locations already exist in Organisation Setup",
        "Re-upload the corrected file",
      ],
      guideLinks: [primary(GUIDE_LINKS.employeeImportGuide), GUIDE_LINKS.employeeImport, GUIDE_LINKS.setup],
    });
  }

  if (
    m.includes("statutory")
    || m.includes("esic")
    || m.includes("lwf")
  ) {
    return notice({
      title: "Statutory details need a fix",
      message,
      hint: "If a statutory ID is filled, it must be valid. Leave a field blank if it does not apply.",
      steps: [
        "Stay on Step 5 — Statutory",
        "Correct the highlighted ID, or clear it and click Finish",
        "Click Finish again",
      ],
      guideLinks: [GUIDE_LINKS.employeeAdd],
    });
  }

  if (m.includes("employment type") || m.includes("employment_type")) {
    return notice({
      title: "Employment type missing",
      message,
      hint: "Create at least one employment type before assigning it to employees.",
      steps: [
        "Open Employee Configuration and create Permanent / Contract / Intern as needed",
        "Return to the employee form and select the type",
        "Save the employee",
      ],
      guideLinks: [primary(GUIDE_LINKS.employmentTypes), GUIDE_LINKS.employeeAdd],
    });
  }

  if (m.includes("nothing was saved") || m.includes("onboard") || m.includes("onboarding failed")) {
    return notice({
      title: "Employee was not saved",
      message,
      hint: "Nothing was written to the database. Fix the issue below and try again.",
      steps: [
        "Read the error message above carefully",
        "Go to the step mentioned and correct the field",
        "Click Finish again",
      ],
      guideLinks: [GUIDE_LINKS.employeeAdd, GUIDE_LINKS.employees],
    });
  }

  // Generic fallback — do NOT push Organisation Setup for every unknown error
  return notice({
    title: "Could not save employee",
    message: message || "We could not save this employee. Please review the form and try again.",
    hint: "No employee was created. Fix the issue, then click Finish again.",
    steps: [
      "Read the message above — it explains what failed",
      "Go back to the relevant step and correct the field",
      "Click Finish again",
    ],
    guideLinks: [GUIDE_LINKS.employeeAdd, GUIDE_LINKS.employees],
  });
}

/** Daily attendance / upload / roster */
function parseAttendance(message) {
  const m = message.toLowerCase();

  if (m.includes("setup") || m.includes("not configured") || m.includes("configuration")) {
    return notice({
      title: "Attendance setup required",
      message,
      hint: "Configure how attendance is captured before marking or importing days.",
      steps: [
        "Open Attendance Setup and complete the wizard",
        "Choose your capture method (manual, biometric, or roster)",
        "Configure working days and holidays if needed",
        "Return and mark attendance again",
      ],
      guideLinks: [primary(GUIDE_LINKS.attendanceSetup), GUIDE_LINKS.attendanceConfig, GUIDE_LINKS.holidays],
    });
  }

  if (m.includes("shift") || m.includes("roster")) {
    return notice({
      title: "Shift / roster needs attention",
      message,
      hint: "Shift-based attendance needs shifts assigned before days can be calculated.",
      steps: [
        "Open Shift Management and create the required shifts",
        "Assign shifts / roster to employees",
        "Return to Attendance and try again",
      ],
      guideLinks: [primary(GUIDE_LINKS.shifts), GUIDE_LINKS.attendanceSetup, GUIDE_LINKS.attendance],
    });
  }

  if (m.includes("organisation") && m.includes("loading")) {
    return notice({
      title: "Organisation still loading",
      message,
      hint: "Wait a moment for organisation data to finish loading.",
      steps: [
        "Wait a few seconds and try again",
        "If it continues, refresh the page",
        "Confirm Attendance Setup is complete",
      ],
      guideLinks: [primary(GUIDE_LINKS.attendanceSetup), GUIDE_LINKS.attendance],
    });
  }

  if (m.includes("employee") && (m.includes("select") || m.includes("required"))) {
    return notice({
      title: "Select an employee first",
      message,
      hint: "Attendance is marked per employee (or bulk-selected list).",
      steps: [
        "Select an employee from the list",
        "If no employees appear, create them in People Hub first",
        "Then mark or save attendance",
      ],
      guideLinks: [primary(GUIDE_LINKS.employees), GUIDE_LINKS.attendanceRecords],
    });
  }

  if (m.includes("holiday") || m.includes("calendar")) {
    return notice({
      title: "Holiday / calendar setup needed",
      message,
      hint: "Gazetted holidays affect attendance and leave sandwich rules.",
      steps: [
        "Open Holiday Calendar and add organisation holidays",
        "Return to Attendance and refresh the period",
      ],
      guideLinks: [primary(GUIDE_LINKS.holidays), GUIDE_LINKS.attendance],
    });
  }

  return notice({
    title: "Attendance action failed",
    message: message || "Could not complete the attendance action.",
    hint: "New organisations should finish Attendance Setup before marking days.",
    steps: [
      "Confirm Attendance Setup is complete",
      "Check the selected employee and date range",
      "Try the action again",
    ],
    guideLinks: [primary(GUIDE_LINKS.attendanceSetup), GUIDE_LINKS.attendance, GUIDE_LINKS.attendanceRecords],
  });
}

/** Leave apply / admin / balances */
function parseLeave(message) {
  const m = message.toLowerCase();

  if (m.includes("status code 500") || m.includes("internal server")) {
    return notice({
      title: "Leave action failed",
      message,
      hint: "The server hit an unexpected error. Retry after checking leave types, balances, and accrual policy.",
      steps: [
        "Reload Leave Balances and try Accrual or Add Balance again",
        "Confirm the leave type has an Accrual Policy",
        "If you were applying leave, retry from Apply Leave after balances load",
      ],
      guideLinks: [primary(GUIDE_LINKS.leaveBalances), GUIDE_LINKS.leaveAdmin, GUIDE_LINKS.leaveApply],
    });
  }

  if (m.includes("accrual")) {
    return notice({
      title: "Monthly accrual could not be posted",
      message,
      hint: "Accrual needs an active leave type and accrual policy for the selected month.",
      steps: [
        "Open Accrual Policy and confirm the leave type is set to accrue",
        "Load the employee balances, then click Accrual again",
        "Use Add Balance if this employee still has no opening days",
      ],
      guideLinks: [primary(GUIDE_LINKS.leaveBalances), GUIDE_LINKS.leaveAdmin],
    });
  }

  if (m.includes("not authorized") || m.includes("not authorised")) {
    return notice({
      title: "You cannot act on this leave",
      message: "This request is waiting for the assigned approver.",
      hint: "Only the person named on the current approval step can approve or reject it.",
      steps: [
        "Open Approve and check who the request is waiting for.",
        "Ask that person to review it, or ask an administrator if the approver is wrong.",
      ],
      guideLinks: [primary({ label: "Approve Leave", to: "/leave?tab=approval" }), GUIDE_LINKS.leave],
    });
  }

  if (m.includes("locked") || m.includes("frozen") || m.includes("payroll finalized")) {
    return notice({
      title: "These dates are already closed",
      message: "Leave cannot be saved because attendance or payroll for these dates is locked.",
      hint: "Choose dates that are still open, or ask an administrator to unlock the period.",
      steps: [
        "Check the From and To dates on the form.",
        "Pick dates that are not locked.",
        "Submit the leave again.",
      ],
      guideLinks: [primary(GUIDE_LINKS.attendanceRecords), GUIDE_LINKS.leave],
    });
  }

  if (m.includes("not initial") || m.includes("initialis") || m.includes("not configured") || m.includes("no leave type") || m.includes("leave type")) {
    return notice({
      title: "Leave module not ready",
      message,
      hint: "Seed leave types and policies before employees can apply.",
      steps: [
        "Open Leave Admin Setup and initialise Indian leave types",
        "Review policies and holiday calendar",
        "Return to Apply Leave and submit again",
      ],
      guideLinks: [primary(GUIDE_LINKS.leaveAdmin), GUIDE_LINKS.holidays, GUIDE_LINKS.leaveApply],
    });
  }

  if (m.includes("balance") || m.includes("insufficient") || m.includes("quota")) {
    return notice({
      title: "Leave balance is not enough",
      message,
      hint: "Check the employee's available balance for this leave type.",
      steps: [
        "Open Leave Balances and verify the employee entitlement",
        "Choose another leave type or shorter dates if needed",
        "Ask HR/admin to adjust balances if this is incorrect",
      ],
      guideLinks: [primary(GUIDE_LINKS.leaveBalances), GUIDE_LINKS.leaveAdmin, GUIDE_LINKS.leaveApply],
    });
  }

  if (m.includes("ux_org_holiday_date") || m.includes("organisation_holidays")) {
    return notice({
      title: "Holiday already on calendar",
      message,
      hint: "This date is already marked as a holiday. Setup can continue — re-apply your leave package if needed.",
      steps: [
        "Open Holiday Calendar to review existing holidays",
        "Return to Leave Admin and apply your leave package again",
        "If the error persists, contact support with the details shown above",
      ],
      guideLinks: [primary(GUIDE_LINKS.holidays), GUIDE_LINKS.leaveAdmin],
    });
  }

  if (
    m.includes("duplicate key") ||
    m.includes("uniqueviolation") ||
    m.includes("unique constraint") ||
    m.includes("sqlalchemy")
  ) {
    return notice({
      title: "Setup partially completed",
      message,
      hint: "Some records already exist from a previous setup attempt.",
      steps: [
        "Apply your leave package again — existing leave types and holidays are preserved",
        "Open Leave Admin to confirm leave types were created",
        "Use Leave Balances to run auto-allocation if balances are missing",
      ],
      guideLinks: [primary(GUIDE_LINKS.leaveAdmin), GUIDE_LINKS.leaveBalances, GUIDE_LINKS.holidays],
    });
  }

  if (m.includes("overlap") || m.includes("conflict") || (m.includes("already") && m.includes("leave"))) {
    return notice({
      title: "Leave dates conflict",
      message,
      hint: "This employee already has leave overlapping these dates.",
      steps: [
        "Pick dates that do not overlap an existing request",
        "Review Leave Balances / history for the employee",
        "Submit again",
      ],
      guideLinks: [primary(GUIDE_LINKS.leaveApply), GUIDE_LINKS.leaveBalances],
    });
  }

  if (m.includes("organisation") || m.includes("org")) {
    return notice({
      title: "Organisation context missing",
      message,
      hint: "Leave needs a valid organisation session.",
      steps: [
        "Refresh the page and sign in again if needed",
        "Confirm Organisation Setup is complete",
        "Initialise Leave Admin if this is a new company",
      ],
      guideLinks: [primary(GUIDE_LINKS.setup), GUIDE_LINKS.leaveAdmin],
    });
  }

  return notice({
    title: "This leave could not be saved",
    message: message || "The leave was not assigned.",
    hint: "Read the message above, adjust the form if it asks you to, then submit again.",
    steps: [
      "Check the employee, leave type, and dates.",
      "Open Leave Balances if the message says there are not enough days.",
      "Submit the leave again.",
    ],
    guideLinks: [primary(GUIDE_LINKS.leaveBalances), GUIDE_LINKS.leave],
  });
}

/** Generic / fallback */
function parseGeneral(message) {
  return notice({
    title: "Something needs your attention",
    message: message || "An unexpected issue occurred.",
    hint: "Use Organisation Setup and module setup wizards if this is a new company.",
    steps: [
      "Read the message carefully and complete any missing setup",
      "Open Organisation Setup if master data is incomplete",
      "Retry the action",
    ],
    guideLinks: [primary(GUIDE_LINKS.setup), GUIDE_LINKS.settings],
  });
}

/**
 * @param {"employee"|"attendance"|"leave"|"payroll"|"general"} domain
 * @param {unknown} errorOrText - axios error, Error, string, or existing notice object
 */
export function buildGuidedNotice(domain, errorOrText) {
  if (errorOrText && typeof errorOrText === "object" && errorOrText.title && Array.isArray(errorOrText.steps)) {
    return {
      title: errorOrText.title,
      message: errorOrText.message || "",
      hint: errorOrText.hint || null,
      steps: errorOrText.steps,
      guideLinks: errorOrText.guideLinks || [],
    };
  }

  // Prefer structured backend detail when present
  const detail = errorOrText?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.code) {
    return notice({
      title: detail.title || "Action could not be completed",
      message: detail.message || extractMessage(errorOrText),
      hint: detail.hint || null,
      steps: Array.isArray(detail.steps) ? detail.steps : [],
      guideLinks: Array.isArray(detail.guideLinks) ? detail.guideLinks : [],
    });
  }

  const message = extractMessage(errorOrText);
  if (!message) return null;
  if (isTechnicalError(message)) return technicalNotice(domain);

  switch (domain) {
    case "employee":
      return parseEmployee(message);
    case "attendance":
      return parseAttendance(message);
    case "leave":
      return parseLeave(message);
    case "payroll":
      // payroll has its own parser; this is a soft fallback
      return notice({
        title: "Payroll needs attention",
        message,
        hint: "Complete Payroll Setup in order, then process again.",
        steps: [
          "Open 3.1 Payroll Settings",
          "Open 3.2 Salary Components",
          "Open 3.3 Salary Structures and assign to employees",
          "Return to Process Payroll",
        ],
        guideLinks: [
          primary(GUIDE_LINKS.payrollSettings),
          GUIDE_LINKS.payrollComponents,
          GUIDE_LINKS.payrollStructures,
          GUIDE_LINKS.assignSalary,
          GUIDE_LINKS.processPayroll,
        ],
      });
    default:
      return parseGeneral(message);
  }
}
